<template>
  <div id="conversion" class="widget widget-height">
    <div class="title row-block">
      <h1>{{ $tr('analytics.widgets.conversion.title') }}</h1>

      <img
        v-tooltip="{
          content: $tr('analytics.widgets.conversion.helper'),
          placement: 'top-center',
          classes: 'tight'
        }"
        class="icon-first canvas-filter"
        src="/static/images/elements/icon_info.svg"
        alt="Info"
      />

      <img class="canvas-filter" src="/static/images/elements/icon_download.svg" alt="Download" @click="saveAsPng" />
    </div>

    <div v-if="loading" class="spinner-wraper">
      <div class="spinner"></div>
    </div>

    <div v-else class="main-content">
      <h1 class="title-thin">{{ currentPercent || '0' }}%</h1>

      <div class="row-block compare">
        <img
          v-if="pastToCurrentComparePercent > 0"
          :src="`/static/images/elements/analytics/stat_up.${imgExtension}`"
          alt="Up"
        />
        <img
          v-else-if="pastToCurrentComparePercent < 0"
          :src="`/static/images/elements/analytics/stat_down.${imgExtension}`"
          alt="Down"
        />

        <span v-if="pastToCurrentComparePercent > 0" class="percentage">
          <i>+</i>{{ pastToCurrentComparePercent }} %
        </span>

        <span v-if="pastToCurrentComparePercent < 0" class="percentage">
          <i>-</i>{{ pastToCurrentComparePercent }} %
        </span>

        <span class="helper">
          {{ hint }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import hintCompareMixin from './hintCompareMixin.js';
import mixinIE11Detector from './mixinIE11Detector.js';
import mixinSaveToPng from './mixinSaveToPng.js';

const isNumeric = n => !isNaN(parseFloat(n)) && isFinite(n);
const calculatePercentage = (a, b) => (a > b ? ((a - b) / a) * 100 : ((a - b) / a) * 100);

const INVITED_STATUS_ID = 3;
const ACCEPTED_TO_WORK_STATUS_ID = 13;

export default {
  name: 'WidgetConversion',

  mixins: [hintCompareMixin, mixinSaveToPng, mixinIE11Detector],

  props: {
    filtersParams: Object,
    filtersPastParams: Object,
    pastDurationMoment: Object
  },

  computed: {
    imgExtension() {
      return this.isIE11 ? 'png' : 'svg';
    },

    acceptedToWorkCount() {
      const accepted = this.responsesByStatuses.find(i => i.status && i.status.id === ACCEPTED_TO_WORK_STATUS_ID);

      return accepted ? accepted.count : 0;
    },

    acceptedToWorkInPastCount() {
      const accepted = this.pastResponsesByStatuses.find(i => i.status && i.status.id === ACCEPTED_TO_WORK_STATUS_ID);

      return accepted ? accepted.count : 0;
    },

    invitedCount() {
      const accepted = this.responsesByStatuses.find(i => i.status && i.status.id === INVITED_STATUS_ID);

      return accepted ? accepted.count : 0;
    },

    invitedInPastCount() {
      const accepted = this.pastResponsesByStatuses.find(i => i.status && i.status.id === INVITED_STATUS_ID);

      return accepted ? accepted.count : 0;
    },

    currentPercent() {
      const a = this.acceptedToWorkCount;
      const b = this.invitedCount;

      const res = (a / b) * 100;

      return res && isNumeric(res) ? res.toFixed(1) : null;
    },

    pastPercent() {
      const a = this.acceptedToWorkInPastCount;
      const b = this.invitedInPastCount;

      const res = (a / b) * 100;

      return res && isNumeric(res) ? res.toFixed(1) : null;
    },

    pastToCurrentComparePercent() {
      const a = this.currentPercent;
      const b = this.pastPercent;

      const res = calculatePercentage(a, b);

      return res && isNumeric(res) ? res.toFixed(1) : null;
    },

    hint() {
      return this.getComparePeriodHint(this.pastDurationMoment, this.pastToCurrentComparePercent);
    },

    ...mapGetters({
      responsesByStatuses: 'analytics/conversion/responsesByStatuses',
      pastResponsesByStatuses: 'analytics/conversion/pastResponsesByStatuses',
      loading: 'analytics/totalInterviewDuration/loading'
    })
  },

  watch: {
    filtersParams() {
      this.fetch();
    },

    filtersPastParams() {
      this.fetchPast();
    }
  },

  beforeMount() {
    this.fetch();
    this.fetchPast();
  },

  methods: {
    fetch() {
      this.$store.dispatch('analytics/conversion/fetch', this.filtersParams);
    },

    fetchPast() {
      this.$store.dispatch('analytics/conversion/fetchCompare', this.filtersPastParams);
    },

    saveAsPng() {
      this.mixinSaveAsPng({
        filename: `conversion_${this.filtersParams.created_after}_${this.filtersParams.created_before}`,
        saveId: 'conversion',
        filterClass: 'canvas-filter'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.widget {
  .spinner-wraper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100px;
  }

  .spinner {
    margin: auto;
    height: 50px;
    width: 50px;
    margin-top: 0;
  }

  .main-content {
    h1.title-thin {
      margin-top: 34px;
      font-weight: 300;

      span {
        line-height: 68px;
        font-size: 48px;
        font-weight: 300;
      }
    }

    h2 {
      margin-top: 4px;
    }

    .row-block.compare {
      margin-top: 16px;

      span.percentage {
        line-height: 25px;
        font-size: 18px;
        white-space: nowrap;
        margin-left: 10px;

        i {
          font-style: normal;
        }

        + .helper {
          margin-left: 14px;
        }
      }

      img {
        width: 16px;
      }

      span.helper {
        line-height: 12px;
        font-size: 12px;
        color: #607183;
      }
    }
  }
}
</style>
